import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MinimalLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Play, 
  Target, 
  CheckCircle,
  AlertCircle,
  Lightbulb,
  User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ElevenLabs voice options
const VOICE_OPTIONS = [
  { id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel', gender: 'female', description: 'Calm & warm' },
  { id: 'pNInz6obpgDQGcFmaJgB', name: 'Adam', gender: 'male', description: 'Deep & confident', image: '/advisor-adam.jpg' },
  { id: 'JKZaVnbf735h6mnfD0fX', name: 'Jerry Citarella', gender: 'male', description: 'Professional & trustworthy', image: '/jerry-ai.png' },
];

export default function ScenarioSetup() {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [difficulty, setDifficulty] = useState(1);
  const [showHints, setShowHints] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState(VOICE_OPTIONS[0]); // Default to female

  useEffect(() => {
    loadScenario();
  }, [scenarioId]);

  const loadScenario = async () => {
    try {
      const response = await axios.get(`${API}/scenarios/${scenarioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScenario(response.data);
      // Auto-select the scenario's default voice if it has one
      if (response.data.default_voice_id) {
        const match = VOICE_OPTIONS.find(v => v.id === response.data.default_voice_id);
        if (match) setSelectedVoice(match);
      }
    } catch (error) {
      toast.error('Failed to load scenario');
      navigate('/scenarios');
    }
    setLoading(false);
  };

  const startSession = async () => {
    setStarting(true);
    try {
      const response = await axios.post(`${API}/sessions`, {
        scenario_id: scenarioId,
        difficulty
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      navigate(`/practice/${response.data.id}`, { 
        state: { scenario, showHints, voiceId: selectedVoice.id, voiceName: selectedVoice.name, voiceImage: selectedVoice.image || null } 
      });
    } catch (error) {
      toast.error('Failed to start session');
    }
    setStarting(false);
  };

  const difficultyLabels = {
    1: { label: 'Easy', desc: 'Supportive responses, gentle pushback', color: 'bg-green-100 text-green-700' },
    2: { label: 'Medium', desc: 'Some resistance, realistic tensions', color: 'bg-yellow-100 text-yellow-700' },
    3: { label: 'Hard', desc: 'Challenging responses, emotional pushback', color: 'bg-red-100 text-red-700' },
  };

  if (loading) {
    return (
      <MinimalLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00E085]"></div>
        </div>
      </MinimalLayout>
    );
  }

  if (!scenario) return null;

  return (
    <div className="min-h-screen bg-white" data-testid="scenario-setup">
      {/* Header */}
      <header className="border-b border-gray-100 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/scenarios" className="text-gray-600 hover:text-gray-900" data-testid="back-to-scenarios">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-semibold text-[#111827]">Scenario Setup</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Scenario Info */}
        <div className="mb-12">
          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-4 ${
            scenario.category === 'spouse' ? 'bg-pink-100 text-pink-700' :
            scenario.category === 'kids' ? 'bg-blue-100 text-blue-700' :
            scenario.category === 'parents' ? 'bg-purple-100 text-purple-700' :
            'bg-amber-100 text-amber-700'
          }`}>
            {scenario.category}
          </span>
          <h2 className="text-3xl font-bold text-[#111827] mb-4">{scenario.title}</h2>
          <p className="text-gray-600 text-lg">{scenario.description}</p>
        </div>

        {/* Your Goal */}
        <div className="bg-[#00E085]/10 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#00E085] rounded-xl flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#111827] mb-2">Your Goal</h3>
              <p className="text-gray-700">{scenario.goal}</p>
            </div>
          </div>
        </div>

        {/* What Success Looks Like */}
        <div className="bg-gray-50 rounded-2xl p-6 mb-8">
          <h3 className="font-semibold text-[#111827] mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[#00E085]" />
            What Success Looks Like
          </h3>
          <ul className="space-y-3">
            {Object.entries(scenario.success_criteria || {}).map(([key, value]) => (
              value && (
                <li key={key} className="flex items-center gap-3 text-gray-700">
                  <div className="w-2 h-2 bg-[#00E085] rounded-full" />
                  {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </li>
              )
            ))}
          </ul>
        </div>

        {/* Difficulty Selection */}
        <div className="mb-8">
          <h3 className="font-semibold text-[#111827] mb-4">Select Difficulty</h3>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((level) => (
              <button
                key={level}
                onClick={() => setDifficulty(level)}
                className={`p-4 rounded-2xl border-2 text-center transition-all ${
                  difficulty === level
                    ? 'border-[#00E085] bg-[#00E085]/5'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
                data-testid={`difficulty-${level}`}
              >
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-2 ${difficultyLabels[level].color}`}>
                  {difficultyLabels[level].label}
                </span>
                <p className="text-sm text-gray-600">{difficultyLabels[level].desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Voice Selection */}
        <div className="mb-8">
          <h3 className="font-semibold text-[#111827] mb-4">Choose AI Voice</h3>
          <div className="grid grid-cols-2 gap-4">
            {VOICE_OPTIONS
              .filter(voice => scenario?.category === 'advisor' ? voice.id === 'JKZaVnbf735h6mnfD0fX' : true)
              .map((voice) => (
              <button
                key={voice.id}
                onClick={() => setSelectedVoice(voice)}
                className={`p-5 rounded-2xl border-2 text-left transition-all ${
                  selectedVoice.id === voice.id
                    ? 'border-[#00E085] bg-[#00E085]/5'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
                data-testid={`voice-${voice.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center gap-4">
                  {voice.image ? (
                    <img src={voice.image} alt={voice.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      voice.gender === 'female' ? 'bg-pink-100' : 'bg-blue-100'
                    }`}>
                      <User className={`w-6 h-6 ${voice.gender === 'female' ? 'text-pink-600' : 'text-blue-600'}`} />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-[#111827]">{voice.name}</p>
                    <p className="text-sm text-gray-500 capitalize">{voice.gender} • {voice.description}</p>
                  </div>
                </div>
                {selectedVoice.id === voice.id && (
                  <div className="mt-3 flex items-center gap-2 text-[#00E085]">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Selected</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Hints Toggle */}
        <div className="bg-gray-50 rounded-2xl p-6 mb-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#6366F1]/20 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-[#6366F1]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#111827]">Show Coaching Hints</h3>
                <p className="text-sm text-gray-500">Get real-time suggestions during practice</p>
              </div>
            </div>
            <Switch
              checked={showHints}
              onCheckedChange={setShowHints}
              data-testid="hints-toggle"
            />
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-50 rounded-2xl p-6 mb-12">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-1">Educational Practice Only</h3>
              <p className="text-sm text-amber-700">
                This is a communication coaching tool for educational purposes. The AI focuses on how you communicate, not what financial decisions you should make. AI-generated responses are not personalized financial advice, and use of this platform does not create an advisor-client relationship.
              </p>
            </div>
          </div>
        </div>

        {/* Start Button */}
        <Button
          onClick={startSession}
          disabled={starting}
          className="w-full h-14 bg-[#00E085] text-[#00331E] hover:bg-[#00C978] rounded-full text-lg font-semibold"
          data-testid="start-session-btn"
        >
          {starting ? (
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 border-2 border-[#00331E]/30 border-t-[#00331E] rounded-full animate-spin" />
              Starting...
            </span>
          ) : (
            <>
              <Play className="w-5 h-5 mr-2" />
              Start Session
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
